import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// One fetch of /auth/me per logged-in user, shared by every component. It carries the effective permission map, computed by the
// same can() logic the backend enforces with, so what is shown can never disagree with what is allowed. The backend still decides.
let cache = { userId: null, promise: null, data: null };

function load(userId) {
  if (cache.userId !== userId || !cache.promise) {
    cache = { userId, data: null, promise: api.get("/auth/me").then(({ data }) => { cache.data = data; return data; }) };
  }
  return cache.promise;
}

export function resetPermissionsCache() {
  cache = { userId: null, promise: null, data: null };
}

// The uppercase actions (VIEW, CREATE, EDIT, DELETE, APPROVE, POST) and the verbs (view, create, edit, delete, approve, certify, post, cancel).
const VERB_FLAG = { view: "VIEW", create: "CREATE", edit: "EDIT", delete: "DELETE", approve: "APPROVE", certify: "APPROVE", post: "POST", cancel: "POST", delete_any: "DELETE" };
const flagOf = (action) => VERB_FLAG[String(action).toLowerCase()] || String(action).toUpperCase();
const keyOf = (module) => String(module).toUpperCase().replace(/[-\s]/g, "_");

/**
 * can(module, action) / canView(module) from the permission map.
 * `module` may be a string (any case: "grn", "PURCHASING"), an array (any of them), or null/undefined (always allowed).
 * `action` is an old action (VIEW ...) or a verb (view, create, edit, approve, post, certify, cancel ...). Default: view.
 * ORG_ADMIN can do everything. While loading, only entries without a module are allowed.
 * level(module) / access(module): the module's level (NONE | VIEW | EDIT | APPROVE | CUSTOM), approval limit, see_rates, can_export.
 * seeRates(module) / canExport(module): the role's switches for that module (the server also hides rates and refuses exports).
 */
export function usePermissions() {
  const { user } = useAuth();
  const [data, setData] = useState(cache.userId === user?.id ? cache.data : null);

  useEffect(() => {
    if (!user?.id) return;
    let alive = true;
    load(user.id).then((d) => alive && setData(d)).catch(() => alive && setData({ permissions: {}, access: {}, failed: true }));
    return () => { alive = false; };
  }, [user?.id]);

  const isAdmin = user?.role === "ORG_ADMIN";
  const can = (module, action = "view") => {
    if (!module) return true;
    if (isAdmin) return true;
    if (!data) return false;
    const flag = flagOf(action);
    const list = Array.isArray(module) ? module : [module];
    return list.some((m) => (data.permissions?.[keyOf(m)] || []).includes(flag));
  };
  const access = (module) => (isAdmin ? { level: "APPROVE", approval_limit: null, see_rates: true, can_export: true } : data?.access?.[keyOf(module)] || null);
  // the two switches that are not allow / deny: may this person see rates, may they print / export, for a module
  const seeRates = (module) => (isAdmin ? true : access(module)?.see_rates !== false);
  const canExport = (module) => (isAdmin ? true : access(module)?.can_export !== false);
  return { loading: !data && !isAdmin, can, canView: (m) => can(m, "view"), level: (m) => access(m)?.level || "NONE", access, seeRates, canExport, data };
}
